import React, { useEffect } from "react";

import Button from "@mui/material/Button";
import {
  FormControl,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
} from "@mui/material";
import { getUserIsLoggedIn, getUsersPackList, createNewPack } from "../utils";
import { Star } from "lucide-react";
import { HEADING_FONT } from "../constants/colors";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";

const Alert = React.forwardRef((props, ref) => {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const PackSelector = ({
  tableId,
  setTableId,
  setTableData,
  setPackName,
  packName,
  defaultPackId,
  setPackDescription,
  handleSelectNewPack,
  packIsLoading,
  setPackSettingsTags,
  setPackAnalytics,
  setPackVisibility,
  setAffiliateLinksAreOff,
  setPackCurrency,
}) => {
  const [userPackList, setUserPackList] = React.useState([]);
  const [packCreatedAlertIsOpen, setPackCreatedAlertIsOpen] =
    React.useState(false);

  useEffect(() => {
    (async () => {
      if (getUserIsLoggedIn()) {
        const packListResponse = await getUsersPackList();
        setUserPackList(packListResponse || []);
      }
    })();
  }, [tableId]);

  // Updates the name in the list to the new pack name
  useEffect(() => {
    const packListToModify = [...userPackList];
    for (let i = 0; i < packListToModify.length; i++) {
      if (packListToModify[i].id === tableId) {
        packListToModify[i].packName = packName;
      }
    }
    setUserPackList(packListToModify);
  }, [packName]);

  const handleSnackbarClose = () => {
    setPackCreatedAlertIsOpen(false);
  };

  const handleCreateNewPack = async () => {
    if (getUserIsLoggedIn()) {
      const {
        packName,
        tableId,
        tableData,
        packDescription,
        isPublic,
        tags,
        packCurrency,
        analytics,
        hasAffiliateLinksTurnedOff,
      } = await createNewPack();
      setTableId(tableId);
      setPackName(packName);
      setTableData(tableData);
      setPackDescription(packDescription);
      setPackCreatedAlertIsOpen(true);
      setPackSettingsTags(tags);
      setPackVisibility(isPublic);
      setPackAnalytics(analytics || {});
      setAffiliateLinksAreOff(hasAffiliateLinksTurnedOff || false);
      setPackCurrency(packCurrency);
    }
  };

  const userIsLoggedIn = getUserIsLoggedIn();

  return (
    <Stack gap={2} sx={{ width: "100%" }}>
      <FormControl fullWidth size="small">
        <InputLabel
          id="demo-simple-select-label"
          sx={{ fontFamily: HEADING_FONT }}
        >
          {userIsLoggedIn ? "My Saved Packs" : "Login to Save Packs"}
        </InputLabel>
        <Select
          key={tableId}
          value={tableId}
          labelId="demo-simple-select-label"
          label={userIsLoggedIn ? "My Saved Packs" : "Login to Save Packs"}
          disabled={!userPackList.length}
          sx={{
            borderRadius: "9px",
            fontFamily: HEADING_FONT,
          }}
          MenuProps={{
            getContentAnchorEl: null,
            anchorOrigin: {
              vertical: "bottom",
            },
            transformOrigin: { horizontal: "left" },
            style: {
              maxHeight: 400,
            },
          }}
          onChange={(e) => {
            handleSelectNewPack(e.target.value);
          }}
        >
          {userPackList.map(({ packName, id, isDefault }) => (
            <MenuItem value={id}>
              {isDefault && id !== tableId ? (
                <>
                  <ListItemIcon>
                    <Star size={18} />
                  </ListItemIcon>
                  <ListItemText primary={packName} />
                </>
              ) : packIsLoading && tableId ? (
                <ListItemIcon>
                  <CircularProgress size={18} />
                </ListItemIcon>
              ) : (
                packName
              )}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        variant="contained"
        disableElevation
        color="success"
        disabled={!userIsLoggedIn}
        sx={{
          borderRadius: "9px",
          fontFamily: HEADING_FONT,
          fontWeight: 600,
          textTransform: "none",
        }}
        onClick={handleCreateNewPack}
      >
        Create New Pack
      </Button>
      <Snackbar
        open={packCreatedAlertIsOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: "250px", color: "white" }}
        >
          New pack created
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default PackSelector;
